import { BrowserModule } from '@angular/platform-browser';
import { NgModule, APP_INITIALIZER } from '@angular/core';
import { RouterModule, Routes, Router } from '@angular/router';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { JwtModule } from '@auth0/angular-jwt';
import { FlexLayoutModule } from '@angular/flex-layout';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatExpansionModule } from '@angular/material/expansion';
import { MaterialModule } from './material.module';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatListModule } from '@angular/material/list';
import { MatDividerModule } from '@angular/material/divider';
import { MatTableModule } from '@angular/material/table';
import {
  MatPaginatorModule,
  MatDialogModule,
  MatNativeDateModule
} from '@angular/material';
import {MatStepperModule} from '@angular/material/stepper';
import { MatSortModule } from '@angular/material/sort';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTabsModule } from '@angular/material/tabs';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { MatTreeModule } from '@angular/material/tree';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { AppComponent } from './app.component';
import { ApiService } from './api.service';
import { ProductAliasComponent } from './productalias.component';
import { NavComponent } from './nav/nav.component';
import { ManufacturerComponent } from './manufacturer/manufacturer.component';
import { ProductComponent } from './product/product.component';
import { OrderListComponent } from './order-list/order-list.component';
import { ProductListComponent } from './product-list/product-list.component';
import { ProductDetailComponent } from './product-detail/product-detail.component';
import { TreeCheckboxComponent } from './tree-checkbox/tree-checkbox.component';
import { LoginComponent } from './login/login.component';
import { RegisterComponent } from './register/register.component';
import { AuthService } from './_service/auth.service';
import { AuthGuard } from './_guard/auth.guard';
import { AuthInterceptor } from './_service/auth.interceptor';
import { AlertifyService } from './_service/alertify.service';
import { UsersComponent } from './users/users.component';
import { UserService } from './_service/user.service';
import { InviteComponent } from './invite/invite.component';
import { UpdateUserComponent } from './users/update-user/update-user.component';
import { CreateUserComponent } from './users/create-user/create-user.component';
import { DeleteUserComponent } from './users/delete-user/delete-user.component';
import { UserPermissionComponent } from './users/user-permission/user-permission.component';
import { UserLocationComponent } from './users/user-location/user-location.component';
import { CompleteuserregistrationComponent } from './completeuserregistration/completeuserregistration.component';
import { Level3authComponent } from './level3auth/level3auth.component';
import { ResetPasswordComponent } from './resetPassword/resetPassword.component';
import { ForgotpasswordComponent } from './forgotpassword/forgotpassword.component';
import { NavResponsiveComponent } from './nav-responsive/nav-responsive.component';
import { HeaderComponent } from './navigation/header/header.component';
import { SidenavListComponent } from './navigation/sidenav-list/sidenav-list.component';
import { ResendEmailComponent } from './resendEmail/resendEmail.component';
import { ShowUserLocationComponent } from './users/user-location/showUserLocation/showUserLocation.component';
import { ProductLocationComponent } from './product-location/product-location.component';
import { UserProfileComponent } from './users/user-profile/user-profile.component';
import { AuthorityComponent } from './authority/authority.component';
import { HasRoleDirective } from './_directives/hasRole.directive';
import { AddSiteComponent } from './authority/add-site/add-site.component';
import { EditSiteComponent } from './authority/edit-site/edit-site.component';
import { AddLocationComponent } from './authority/add-location/add-location.component';
import { AddDepartmentComponent } from './authority/add-department/add-department.component';
import { ReportComponent } from './report/report.component';
import { MatRadioModule } from '@angular/material/radio';
import { GenerateComponent } from './report/generate/generate.component';
import { ScheduleComponent } from './report/schedule/schedule.component';
import { SearchComponent } from './search/search.component';
import { ProductPageComponent } from './search/product-page/product-page.component';
import { MatGridListModule } from '@angular/material/grid-list';
import { RequestComponent } from './request/request.component';
import { UserReportComponent } from './report/user-report/user-report.component';
import { CompanyReportComponent } from './report/company-report/company-report.component';
import { ReportMenuComponent } from './report/report-menu/report-menu.component';
import { AuthLevelsComponent } from './auth-levels/auth-levels.component';
import { HelpComponent } from './help/help.component';
import { ContactUsComponent } from './contact-us/contact-us.component';
import { RequestConfirmComponent } from './request/request-confirm/request-confirm.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { InventoryProductComponent } from './inventory-product/inventory-product.component';
import { CreateProductComponent } from './inventory-product/create-product/create-product.component';
import { EditProductComponent } from './inventory-product/edit-product/edit-product.component';
import { ManufacturerfilterComponent } from './filter/manufacturerfilter/manufacturerfilter.component';
import { LocationfilterComponent } from './filter/locationfilter/locationfilter.component';
import { ProductCasfilterComponent } from './filter/productCasfilter/productCasfilter.component';
import { CreateManufacturerComponent } from './manufacturer/create-manufacturer/create-manufacturer.component';
import { EditManufacturerComponent } from './manufacturer/edit-manufacturer/edit-manufacturer.component';
import { ContactLocationComponent } from './manufacturer/contactLocation/contactLocation.component';
import { InventoryDetailComponent } from './inventory-product/inventory-detail/inventory-detail.component';
import { ExportListComponent } from './inventory-product/exportList/exportList.component';
import { ProductLabelComponent } from './product-label/product-label.component';
import { AddSubDepartmentComponent } from './authority/add-subDepartment/add-subDepartment.component';
import { AddFloorComponent } from './authority/add-floor/add-floor.component';
import { LocationTreeComponent } from './locationTree/locationTree.component';
import { AuthorityTreeComponent } from './AuthorityTree/AuthorityTree.component';
import { CreateSecondarynameComponent } from './inventory-product/create-secondaryname/create-secondaryname.component';
import { UpdateSecondarynameComponent } from './inventory-product/update-secondaryname/update-secondaryname.component';
import { PaginationService } from './_service/pagination.service';
import { ContactUsService } from './_service/contactUs.service';
import { FooterComponent } from './navigation/footer/footer.component';
import { InventoryVendorapiDetailComponent } from './inventory-product/inventory-vendorapi-detail/inventory-vendorapi-detail.component';
import { TreeComponent } from './tree/tree.component';
import { UserUpdatepasswordComponent } from './users/user-updatepassword/user-updatepassword.component';
import { AddAuthorityComponent } from './authority/add-authority/add-authority.component';
// import { MatMomentDateModule, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { SiteReportComponent } from './report/site-report/site-report.component';
import { FileuploadComponent } from './fileupload/fileupload.component';
import { ManufacturerServerSearchComponent } from './manufacturerServerSearch/manufacturerServerSearch.component';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { PhysicalhazardComponent } from './report/reportcomponents/physicalhazard/physicalhazard.component';
import { HealthhazardComponent } from './report/reportcomponents/healthhazard/healthhazard/healthhazard.component';
import { ProductchemicalComponent } from './report/reportcomponents/productchemical/productchemical/productchemical.component';
import { ProductinfoComponent } from './report/reportcomponents/productinfo/productinfo/productinfo.component';
import { ManufacturerinfoComponent } from './report/reportcomponents/manufacturer/manufacturerinfo/manufacturerinfo.component';
import { ConfirmationDialogueComponent } from './inventory-product/inventory-detail/inventoryConfirmation/confirmationDialogue/confirmationDialogue.component';
import { DeleteConfirmationComponent } from './inventory-product/edit-product/deleteConfirmation/deleteConfirmation/deleteConfirmation.component';
import { AuthConfigService } from './_service/auth-config.service';
import { SdsViewComponent } from './sds-view/sds-view.component';

export function tokenGetter() {
  return localStorage.getItem('token');
}

export function initializeAuthMode(authConfigService: AuthConfigService) {
  return (): Promise<any> => {
    return authConfigService.initializeAuthMode();
  };
}

export const routes: Routes = [
  {
    path: '',
    runGuardsAndResolvers: 'always',
    canActivate: [AuthGuard],
    children: [
      { path: 'product-location', component: ProductLocationComponent, data: {roles: ['Super Admin', 'Site Admin']} },
      { path: 'user-profile/:id', component: UserProfileComponent, data: {roles: ['Super Admin', 'Site Admin']} },
      { path: 'authority', component: AuthorityComponent, data: {roles: ['Super Admin', 'Site Admin']} },
      { path: 'users', component: UsersComponent, data: {roles: ['Super Admin', 'Site Admin']} },
      { path: 'create-user', component: CreateUserComponent, data: {roles: ['Super Admin', 'Site Admin']} },
      { path: 'update-user/:id', component: UpdateUserComponent, data: {roles: ['Super Admin', 'Site Admin']} },
      { path: 'delete-user', component: DeleteUserComponent, data: {roles: ['Super Admin', 'Site Admin']} },
      { path: 'user-permission/:id', component: UserPermissionComponent, data: {roles: ['Super Admin', 'Site Admin']} },
      { path: 'user-location/:id', component: UserLocationComponent, data: {roles: ['Super Admin', 'Site Admin']} },
      { path: 'showUserLocation/:id', component: ShowUserLocationComponent, data: {roles: ['Super Admin', 'Site Admin']} },
      { path: 'invite', outlet: 'modal', component: InviteComponent, data: {roles: ['Super Admin', 'Site Admin']} },
      { path: 'resendEmail/:id', component: ResendEmailComponent, data: {roles: ['Super Admin', 'Site Admin']} },
      { path: 'request', component: RequestComponent, data: {roles: ['Super Admin', 'Site Admin', 'Regular']} },
      { path: 'inventory-product', component: InventoryProductComponent, data: {roles: ['Super Admin', 'Site Admin']} },
      { path: 'edit-product', component: EditProductComponent, data: {roles: ['Super Admin']} },
      { path: 'create-product', component: CreateProductComponent, data: {roles: ['Super Admin']} },
      { path: 'authorityTree', component: AuthorityTreeComponent, data: {roles: ['Super Admin', 'Site Admin']} },
      { path: 'report', component: ReportComponent, data: {roles: ['Super Admin', 'Site Admin']} },
      { path: 'user-report', component: UserReportComponent, data: {roles: ['Super Admin', 'Site Admin']} },
      { path: 'site-report', component: SiteReportComponent, data: {roles: ['Super Admin', 'Site Admin']} },
      { path: 'companies', component: ManufacturerComponent, data: {roles: ['Super Admin']}},
      { path: 'company-report', component: CompanyReportComponent, data: {roles: ['Super Admin']} },
    ]
  },

  { path: 'completeuserregistration', outlet: 'modal', component: CompleteuserregistrationComponent },
  { path: 'completeuserregistration', component: CompleteuserregistrationComponent },
  { path: 'search', component: SearchComponent },
  { path: 'login', outlet: 'modal', component: LoginComponent },
  { path: 'resetPassword', outlet: 'modal', component: ResetPasswordComponent },
  { path: 'resetPassword', component: ResetPasswordComponent },
  { path: 'forgotpassword', component: ForgotpasswordComponent },
  // { path: 'help', component: HelpComponent },
  { path: 'contact', component: ContactUsComponent },
  { path: 'view/:id', component: SdsViewComponent },

  { path: '', redirectTo: '/search', pathMatch: 'full' },
  { path: '**', redirectTo: '/search', pathMatch: 'full' },
];

@NgModule({
   declarations: [
      AppComponent,
      ProductAliasComponent,
      NavComponent,
      ManufacturerComponent,
      ProductComponent,
      OrderListComponent,
      ProductListComponent,
      CreateProductComponent,
      ProductDetailComponent,
      TreeCheckboxComponent,
      LoginComponent,
      RegisterComponent,
      InviteComponent,
      UsersComponent,
      UserPermissionComponent,
      UserLocationComponent,
      ShowUserLocationComponent,
      UpdateUserComponent,
      CreateUserComponent,
      DeleteUserComponent,
      CompleteuserregistrationComponent,
      UserProfileComponent,
      ForgotpasswordComponent,
      ResetPasswordComponent,
      ResendEmailComponent,
      Level3authComponent,
      NavResponsiveComponent,
      SearchComponent,
      HeaderComponent,
      FooterComponent,
      SidenavListComponent,
      ProductLocationComponent,
      HasRoleDirective,
      AddSiteComponent,
      AddAuthorityComponent,
      AuthorityComponent,
      EditSiteComponent,
      AddLocationComponent,
      AddDepartmentComponent,
      AddSubDepartmentComponent,
      AddFloorComponent,
      ReportComponent,
      SiteReportComponent,
      GenerateComponent,
      ScheduleComponent,
      ProductPageComponent,
      RequestComponent,
      UserReportComponent,
      CompanyReportComponent,
      ReportMenuComponent,
      AuthLevelsComponent,
      HelpComponent,
      ContactUsComponent,
      RequestConfirmComponent,
      PageNotFoundComponent,
      InventoryProductComponent,
      EditProductComponent,
      ManufacturerfilterComponent,
      LocationfilterComponent,
      ProductCasfilterComponent,
      CreateManufacturerComponent,
      EditManufacturerComponent,
      ContactLocationComponent,
      InventoryDetailComponent,
      InventoryVendorapiDetailComponent,
      ExportListComponent,
      ProductLabelComponent,
      LocationTreeComponent,
      AuthorityTreeComponent,
      CreateSecondarynameComponent,
      UpdateSecondarynameComponent,
      TreeComponent,
      UserUpdatepasswordComponent,
      EditManufacturerComponent,
      FileuploadComponent,
      ManufacturerServerSearchComponent,
      PhysicalhazardComponent,
      HealthhazardComponent,
      ProductchemicalComponent,
      ProductinfoComponent,
      ManufacturerinfoComponent,
      ConfirmationDialogueComponent,
      DeleteConfirmationComponent,
      SdsViewComponent
   ],
   imports: [
      BrowserModule,
      FormsModule,
      FlexLayoutModule,
      ReactiveFormsModule.withConfig({ warnOnNgModelWithFormControl: 'never' }),
      RouterModule.forRoot(routes),
      BrowserAnimationsModule,
      MaterialModule,
      MatButtonModule,
      MatCardModule,
      MatFormFieldModule,
      MatInputModule,
      HttpClientModule,
      MatToolbarModule,
      MatListModule,
      MatDividerModule,
      MatTableModule,
      MatPaginatorModule,
      MatSortModule,
      MatCheckboxModule,
      MatTabsModule,
      MatIconModule,
      MatProgressSpinnerModule,
      MatMenuModule,
      MatTreeModule,
      MatTooltipModule,
      MatSelectModule,
      MatAutocompleteModule,
      MatDialogModule,
      MatGridListModule,
      MatDatepickerModule,
      MatNativeDateModule,
      MatRadioModule,
      MatExpansionModule,
      MatProgressBarModule,
      MatStepperModule,
      MatDatepickerModule,
      // MatMomentDateModule,
      NgxMatSelectSearchModule,
      JwtModule.forRoot({
        config: {
          tokenGetter: tokenGetter,
          // whitelistedDomains: ['https://localhost:44308']
          // whitelistedDomains: ['https://ohssandboxqa.healthcarebc.ca']
          whitelistedDomains: ['https://sds.healthcarebc.ca']
        }
      })
 ],
 providers: [
   AuthGuard,
   ApiService,
   AuthService,
   AuthConfigService,
   UserService,
   ContactUsService,
   AlertifyService,
   AuthInterceptor,
   MatDatepickerModule,
   MatNativeDateModule,
   PaginationService,
   {
     provide: HTTP_INTERCEPTORS,
     useClass: AuthInterceptor,
     multi: true
   },
  {
     provide: APP_INITIALIZER,
     useFactory: initializeAuthMode,
     deps: [AuthConfigService],
     multi: true
   },
 ],
 bootstrap: [AppComponent],
 entryComponents: [
   AddSiteComponent,
   AddAuthorityComponent,
   EditSiteComponent,
   AddLocationComponent,
   AddDepartmentComponent,
   AddSubDepartmentComponent,
   AddFloorComponent,
   GenerateComponent,
   ScheduleComponent,
   AddLocationComponent,
   ProductPageComponent,
   RequestConfirmComponent,
   ProductLocationComponent,
   CreateManufacturerComponent,
   InventoryDetailComponent,
   InventoryVendorapiDetailComponent,
   EditManufacturerComponent,
   ExportListComponent,
   ProductLabelComponent,
   UserProfileComponent,
   LoginComponent,
   CreateSecondarynameComponent,
   UpdateSecondarynameComponent,
   UserUpdatepasswordComponent,
   ResetPasswordComponent,
   CompleteuserregistrationComponent,
   EditManufacturerComponent,
   ConfirmationDialogueComponent,
   DeleteConfirmationComponent
   // ResendEmailComponent
 ]
})
export class AppModule {
  constructor(router: Router) {
    // Use a custom replacer to display function names in the route configs
    // const replacer = (key, value) => (typeof value === 'function') ? value.name : value;
    // console.log('Routes: ', JSON.stringify(router.config, replacer, 2));
  }
}
